import React, { Fragment, useEffect, useRef, useState } from 'react'
import { GetServerSideProps, GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import DefaultLayout from '../../layouts/DefaultLayout'
import { Api } from '../../../types/api'
import Collection = Api.Collection
import Training = Api.Training
import { fetcher } from '../../api/io'
import NoItems from '../../components/elements/NoItems'
import TrainingCard from '../../components/elements/cards/TrainingCard'
import { routes } from '../../utils/routes'
import Link from 'next/link'
import TrainingProvider = Api.TrainingProvider
import Pagination from '../../components/elements/Pagination'
import Topic = Api.Topic
import { useRouter } from 'next/router'
import { Menu, Transition } from '@headlessui/react'
import Checkbox from '../../components/elements/inputs/Checkbox'
import Button from '../../components/elements/Button'
import BackButton from '../../components/elements/BackButton'
import Slider from '../../components/elements/inputs/Slider'
import { XIcon } from '@heroicons/react/outline'

import DynamicHeroIcon from '../../components/elements/DynamicHeroIcon'
import Head from 'next/head'
import { randomBytes } from 'crypto'

interface ServerSideProps {
    trainings: Collection<Training> | null
    topics: Collection<Topic> | null
    trainingProviders: Collection<TrainingProvider> | null
    orderRandom: number
}

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async (context: GetServerSidePropsContext) => {
    context.res.setHeader('Cache-Control', 'no-store')
    let orderRandom = Math.random()
    const { q, provider, skill, topic, studyloadlte, page, priceType, locationType, order, r } = context.query
    const searchParams = new URLSearchParams()
    if (page && page !== '') searchParams.append('page', page.toString())

    let queryProviders: string[] = []
    if (provider) {
        queryProviders = typeof provider === 'string' ? [provider] : provider
    }
    let querySkills: string[] = []
    if (skill) {
        querySkills = typeof skill === 'string' ? [skill] : skill
    }

    let queryTopics: string[] = []
    if (topic) {
        queryTopics = typeof topic === 'string' ? [topic] : topic
    }

    if (r) {
        orderRandom = typeof r === 'string' ? Number(r) : Number(r[0])
    }

    let queryStudyload: string[] = []
    if (studyloadlte) {
        queryStudyload = typeof studyloadlte === 'string' ? [studyloadlte] : studyloadlte
    }

    let queryPriceType: string[] = []
    if (priceType) {
        queryPriceType = typeof priceType === 'string' ? [priceType] : priceType
    }
    let queryLocationType: string[] = []
    if (locationType) {
        queryLocationType = typeof locationType === 'string' ? [locationType] : locationType
    }

    let queryOrder: string[] = ['random']
    if (order) {
        queryOrder = typeof order === 'string' ? [order] : order
    }

    let trainings: Collection<Training> | null = null
    let topics: Collection<Topic> | null = null
    let trainingProviders: Collection<TrainingProvider> | null = null

    try {
        if (q && q !== '') searchParams.append('q', q.toString())

        queryProviders.forEach((item) => {
            searchParams.append('provider[]', item)
        })
        querySkills.forEach((item) => {
            searchParams.append('skills[]', item)
        })
        queryTopics.forEach((item) => {
            searchParams.append('topic[]', item)
        })
        queryStudyload.forEach((item) => {
            searchParams.append('studyLoad[lte]', item)
        })
        queryPriceType.forEach((item) => {
            searchParams.append('priceType[]', item)
        })
        queryLocationType.forEach((item) => {
            searchParams.append('locationType[]', item)
        })

        switch (queryOrder[0]) {
            case 'title':
                searchParams.append('order[title]', 'asc')
                break
            case 'statistics.visitedTrainingsCount':
                searchParams.append('order[statistics.visitedTrainingsCount]', 'desc')
                break
            case 'createdAt':
                searchParams.append('order[createdAt]', 'desc')
                break
            case 'relevance':
                searchParams.append('order[relevance]', 'asc')
                break
            default:
                searchParams.append('order[random]', orderRandom.toString())
                break
        }

        searchParams.append('studyLoad[gt]', '3600')

        console.log('Search params:', searchParams.toString())

        trainings = await fetcher<null, Collection<Training>>({
            url: '/trainings/search?' + searchParams.toString() + '&thumbnail=large',
        })

        topics = await fetcher<null, Collection<Topic>>({
            url: '/topics',
        })

        trainingProviders = await fetcher<null, Collection<TrainingProvider>>({
            url: '/training_providers?order[name]&hasPublishedTrainings=true&itemsPerPage=150',
        })
        // console.log('trainings', trainings)
    } catch (e) {
        console.log('error', e)
    }

    return {
        props: {
            ...(await serverSideTranslations(context.locale ? context.locale : '', [
                'common',
                'trainings',
                'topics',
                'search',
                'meta',
            ])),
            trainings,
            topics,
            trainingProviders,
            orderRandom,
        },
    }
}

const TrainingIndex = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const { t } = useTranslation(['common', 'trainings', 'topic', 'search'])
    const isInitialMount = useRef(true)
    const isInitialMountTopic = useRef(true)
    const router = useRouter()
    const { q, provider, skill, topic, studyloadlte, priceType, locationType, order } = router.query

    let queryProviders: string[] = []
    if (provider) {
        queryProviders = typeof provider === 'string' ? [provider] : provider
    }

    let querySkills: string[] = []
    if (skill) {
        querySkills = typeof skill === 'string' ? [skill] : skill
    }

    let queryTopics: string[] = []
    if (topic) {
        queryTopics = typeof topic === 'string' ? [topic] : topic
    }

    let queryStudyloadLTE: string[] = ['157680000']
    if (studyloadlte) {
        queryStudyloadLTE = typeof studyloadlte === 'string' ? [studyloadlte] : studyloadlte
    }

    let queryPriceType: string[] = []
    if (priceType) {
        queryPriceType = typeof priceType === 'string' ? [priceType] : priceType
    }

    let queryLocationType: string[] = []
    if (locationType) {
        queryLocationType = typeof locationType === 'string' ? [locationType] : locationType
    }

    let queryOrder: string[] = ['default']
    if (order) {
        queryOrder = typeof order === 'string' ? [order] : order
    }

    // Check if filter must be shown
    // const showFilterState = !!router.query.showFilter
    const sortOptions = ['default', 'createdAt', 'title', 'statistics.visitedTrainingsCount', 'relevance']
    const [selectedSortOption, setSelectedSortOption] = useState<string[]>(queryOrder)
    const [orderRandom, setOrderRandom] = useState<number>(props.orderRandom)
    const [showFilter, setShowFilter] = useState<boolean>(false)
    const [selectedTrainingProvider, setSelectedTrainingProvider] = useState<string[]>(queryProviders)
    const [selectedSkills] = useState<string[]>(querySkills)
    const [selectedTopic, setSelectedTopic] = useState<string[]>(queryTopics)
    const [selectedStudyload, setSelectedStudyLoad] = useState<string[]>(queryStudyloadLTE)
    const [selectedPriceType, setSelectedPriceType] = useState<string[]>(queryPriceType)
    const [selectedLocationType, setSelectedLocationType] = useState<string[]>(queryLocationType)

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false
        } else {
            const changeUrl = async () => {
                const searchParams = new URLSearchParams()
                if (q && q !== '') searchParams.append('q', q.toString())

                selectedSkills.forEach((item) => {
                    searchParams.append('skill', item)
                })

                selectedTrainingProvider.forEach((item) => {
                    searchParams.append('provider', item)
                })

                selectedTopic.forEach((item) => {
                    searchParams.append('topic', item)
                })

                selectedStudyload.forEach((item) => {
                    searchParams.append('studyloadlte', item)
                })

                selectedPriceType.forEach((item) => {
                    searchParams.append('priceType', item)
                })
                selectedLocationType.forEach((item) => {
                    searchParams.append('locationType', item)
                })

                selectedSortOption.forEach((item) => {
                    searchParams.append('order', item)
                })

                searchParams.append('r', orderRandom.toString())

                // searchParams.append('showFilter', 'true')

                await router.replace(routes.trainings.path + '?' + searchParams.toString())
            }

            changeUrl().catch((e) => {
                console.log(e)
            })
        }
    }, [
        selectedSkills,
        selectedTrainingProvider,
        selectedTopic,
        selectedStudyload,
        selectedPriceType,
        selectedLocationType,
        selectedSortOption,
    ])

    useEffect(() => {
        if (isInitialMountTopic.current) {
            isInitialMountTopic.current = false
        } else {
            const queryTopics: string[] = []
            if (!topic) {
                // queryTopics = typeof topic === 'string' ? [topic] : topic
                setSelectedTopic(queryTopics)
            }
        }
    }, [topic])

    const changeProviderFilter = (uuid: string) => {
        if (!selectedTrainingProvider.includes(uuid)) {
            setSelectedTrainingProvider([...selectedTrainingProvider, uuid])
        } else {
            setSelectedTrainingProvider(selectedTrainingProvider.filter((item) => item !== uuid))
        }
    }

    const changeTopicFilter = (uuid: string) => {
        if (!selectedTopic.includes(uuid)) {
            setSelectedTopic([uuid])
        } else {
            setSelectedTopic(selectedTopic.filter((item) => item !== uuid))
        }
    }

    const changeStudyloadFilter = (value: string) => {
        setSelectedStudyLoad([value])
    }

    const changePriceTypeFilter = () => {
        if (!selectedPriceType.includes('free')) {
            setSelectedPriceType([...selectedPriceType, 'free'])
        } else {
            setSelectedPriceType(selectedPriceType.filter((item) => item !== 'free'))
        }
    }
    const changeLocationTypeFilter = (value: string) => {
        if (!selectedLocationType.includes(value)) {
            setSelectedLocationType([...selectedLocationType, value])
        } else {
            setSelectedLocationType(selectedLocationType.filter((item) => item !== value))
        }
    }
    const nonce = randomBytes(128).toString('base64')
    const csp = `object-src 'none'; base-uri 'none'; script-src 'self' 'unsafe-eval' 'unsafe-inline' https: http: 'nonce-${nonce}' 'strict-dynamic'`

    return (
        <>
            {selectedTopic.length === 0 && (
                <Head>
                    <title>{t('opleidingsaanbod.title', { ns: 'meta' })}</title>
                    <meta name="description" content={t('opleidingsaanbod.description', { ns: 'meta' })} />
                </Head>
            )}
            {selectedTopic.length > 0 && (
                <Head>
                    <title>{t('topic.' + selectedTopic[0] + '.title', { ns: 'meta' })}</title>
                    <meta name="description" content={t('topic.' + selectedTopic[0] + 'description', { ns: 'meta' })} />
                </Head>
            )}
            <DefaultLayout noMain={true}>
                <main className="space-y-6 overflow-x-hidden min-h-[calc(100vh-var(--dann-navbar-height-lg))]">
                    <div className="flex">
                        <div className="flex-grow p-4 lg:p-8 space-y-6 mt-[var(--dann-navbar-height-lg)]">
                            <BackButton />

                            {props.topics && props.topics['hydra:member'] && props.topics['hydra:member'].length !== 0 && (
                                <div className="">
                                    <div className="flex items-center justify-between">
                                        <h2>{t('topics', { ns: 'topics' })} </h2>
                                        <Button variant="secondary" onClick={() => setShowFilter(!showFilter)}>
                                            {showFilter
                                                ? t('hideFilters', { ns: 'search' })
                                                : t('showFilters', { ns: 'search' })}
                                        </Button>
                                    </div>
                                    <div className="space-y-6 mb-16">
                                        <p className="block max-w-prose">
                                            Onderstaande thema’s zijn een hulpmiddel voor jouw zoektocht naar een
                                            passende opleiding. Kies een van de onderstaande thema's om het volledige
                                            opleidingsaanbod te filteren of maak gebruik van de overige sorteer- en
                                            filteropties. Mogelijk helpt de <Link href={'/'}>Startpagina</Link> jou ook
                                            in jouw zoektocht met aanbod op ieder niveau.
                                        </p>
                                        <div className="flex flex-wrap gap-x-4 gap-y-4 ">
                                            {props.topics?.['hydra:member'].map((topic: Topic) => (
                                                <a
                                                    className={`hover:no-underline border border-transparent cursor-pointer rounded-full py-1 px-4 md:py-2 md:px-4 select-none ${
                                                        selectedTopic.includes(topic['@id'])
                                                            ? 'bg-crusta-500 text-white hover:border-gray-200 hover:text-crusta-100 shadow'
                                                            : 'bg-gray-100 text-gray-700 hover:border-crusta-500 '
                                                    }`}
                                                    key={topic.uuid}
                                                    onClick={() => changeTopicFilter(topic['@id'])}
                                                >
                                                    <span className="theme">{topic.title}</span>
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center justify-between">
                                <div>
                                    <h2>
                                        {t('trainings-title')}{' '}
                                        {selectedTopic.map(
                                            (item) =>
                                                props.topics?.['hydra:member'].find((topic) => topic['@id'] === item)
                                                    ?.title
                                        )}
                                    </h2>
                                </div>
                                <div>
                                    <Menu as="div" className="relative">
                                        <div>
                                            <Menu.Button className="flex  items-center ml-8 text-sm bg-white rounded-full focus:outline-none ">
                                                <span className="sr-only">{t('open-sort')}</span>
                                                <span className="hidden md:block mr-1 font-medium">Gesorteerd op:</span>
                                                <span className="mr-1">
                                                    {t('order-' + selectedSortOption[0], { ns: 'trainings' })}
                                                </span>
                                                <DynamicHeroIcon
                                                    icon="ChevronDownIcon"
                                                    className="w-4 h-4 "
                                                    aria-hidden="true"
                                                />
                                            </Menu.Button>
                                        </div>
                                        <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-200"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                        >
                                            <Menu.Items className="absolute z-50 divide-y divide-gray-100 right-0 mt-2 w-48 overflow-hidden bg-white rounded-md ring-1 shadow-lg origin-top-right ring-blacks-50 focus:outline-none">
                                                <div>
                                                    {sortOptions.map((item) => (
                                                        <Menu.Item>
                                                            {({ active }) => (
                                                                <button
                                                                    className={
                                                                        active
                                                                            ? 'bg-gray-100 block px-4 py-2 text-sm text-gray-700 hover:no-underline w-full text-left'
                                                                            : 'block px-4 py-2 text-sm text-gray-700 hover:no-underline w-full text-left'
                                                                    }
                                                                    onClick={() => setSelectedSortOption([item])}
                                                                >
                                                                    {t('order-' + item, { ns: 'trainings' })}
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    ))}
                                                </div>
                                            </Menu.Items>
                                        </Transition>
                                    </Menu>
                                </div>
                            </div>

                            {props.trainings === null ||
                            props.trainings?.['hydra:member'] === undefined ||
                            props.trainings?.['hydra:totalItems'] === 0 ? (
                                <NoItems
                                    keepSmall={true}
                                    iconName="AcademicCapIcon"
                                    title={t('no-items-title', { ns: 'trainings' })}
                                    description={t('no-items-description-public', { ns: 'trainings' })}
                                />
                            ) : (
                                <>
                                    <div className="grid gap-8 grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
                                        {props.trainings?.['hydra:member'].map((training: Training) => (
                                            <Link
                                                href={routes.trainings.path + '/' + training.uuid}
                                                key={training.uuid}
                                            >
                                                <a className="hover:no-underline">
                                                    <TrainingCard
                                                        title={training.title}
                                                        providerName={training.provider ? training.provider.name : ''}
                                                        src={`${
                                                            training.image
                                                                ? process.env.NEXT_PUBLIC_URL +
                                                                  training.image.thumbnailUrl
                                                                : ''
                                                        }`}
                                                    />
                                                </a>
                                            </Link>
                                        ))}
                                    </div>

                                    <Pagination totalCount={props.trainings['hydra:totalItems']} pageSize={30} />
                                </>
                            )}
                        </div>
                        <Transition
                            show={showFilter}
                            className="fixed inset-0 overflow-y-auto md:relative bg-gray-100 mt-[var(--dann-navbar-height-lg)] min-h-[calc(100vh-var(--dann-navbar-height-lg))] flex-shrink-0 lg:w-1/4"
                            enter="transition-all ease-in-out duration-300"
                            enterFrom="w-0"
                            enterTo="w-full lg:w-1/4"
                            leave="transition-all duration-150 delay-100"
                            leaveFrom="w-full lg:w-1/4"
                            leaveTo="w-0"
                        >
                            <Transition.Child
                                className="p-8"
                                enter="transition-all ease-in duration-400 delay-200"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="transition-all duration-50"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="flex mb-4 md:hidden justify-end">
                                    <button
                                        className="flex text-mariner-500 transition-colors duration-200 hover:text-gray-400"
                                        onClick={() => setShowFilter(false)}
                                    >
                                        <XIcon className="w-6 h-6 " />
                                        Sluit filters
                                    </button>
                                </div>

                                <h2>Filter op:</h2>
                                <h3 className="mt-6 mb-2 text-lg">{t('priceType-title', { ns: 'trainings' })}</h3>
                                <div className="space-y-2">
                                    <Checkbox
                                        label={t('free')}
                                        value={''}
                                        onChange={() => changePriceTypeFilter()}
                                        checked={selectedPriceType.includes('free')}
                                    />
                                </div>
                                <h3 className="mt-6 mb-2 text-lg">{t('locationType-title', { ns: 'trainings' })}</h3>
                                <div className="space-y-2">
                                    <Checkbox
                                        label={t('online')}
                                        value={''}
                                        onChange={() => changeLocationTypeFilter('online')}
                                        checked={selectedLocationType.includes('online')}
                                    />
                                    <Checkbox
                                        label={t('on_location')}
                                        value={''}
                                        onChange={() => changeLocationTypeFilter('on_location')}
                                        checked={selectedLocationType.includes('on_location')}
                                    />
                                </div>
                                <h3 className="mt-6 mb-2 text-lg">{t('studyLoad-title', { ns: 'trainings' })}</h3>
                                <div className="space-y-2">
                                    <label>{t('studyLoad-label', { ns: 'trainings' })}</label>
                                    <Slider
                                        name="Filter op Studiebelasting"
                                        onChange={(studyLoad) => changeStudyloadFilter(studyLoad)}
                                        value={selectedStudyload[0]}
                                    />
                                </div>
                                <h3 className="mt-6 mb-2 text-lg">Aanbieder</h3>
                                <div className="space-y-2">
                                    {props.trainingProviders?.['hydra:member'] &&
                                        props.trainingProviders?.['hydra:member'].map((trainingProvider) => (
                                            <Checkbox
                                                key={trainingProvider.uuid}
                                                name={trainingProvider.uuid}
                                                label={trainingProvider.name}
                                                value={trainingProvider.uuid}
                                                onChange={() => changeProviderFilter(trainingProvider.uuid)}
                                                checked={selectedTrainingProvider.includes(trainingProvider.uuid)}
                                            />
                                        ))}
                                </div>
                            </Transition.Child>
                        </Transition>
                    </div>
                </main>
            </DefaultLayout>
        </>
    )
}

export default TrainingIndex
